// ─── Apply Test Results (z8nrz7g2wm) ─────────────────────────────────────────
//
// Laboratory values typed on the form by hand — no LIS automation, or the
// connection is down — handed to one product with a signature. These rules say
// which values a stage covers, which of them must be filled, and which products
// may receive them.
//
// The backend's `lis-manual-results.ts` (cgt-backend src/integrations/lis) is
// the authority and refuses the same things; this copy lets the dialog flag the
// empty fields before a signature is asked for. Same targets, same rule.
//
// Pure on purpose: `vitest.config.ts` matches only `src/**/*.test.ts`.

import {
  isConstantField,
  valueIsAttachment,
  type FormDefinition,
  type TableConfig,
} from '@/types/cgt'
import type { CreatedProduct } from '@/types/products'
import { allFields } from '@/lib/formSections'
import { asTableConfig } from '@/lib/mappingTargets'
import { cellKey, resolveCell } from '@/lib/tableForm'
import { isSuperseded, isUsedUp } from '@/lib/productSupersession'
import { lisStageOfTarget, lisTestCodeOfTarget } from '@/lib/lis/fieldStage'

/** Field types that never hold a typed laboratory value. */
const NOT_A_VALUE = new Set(['section', 'calculated', 'process', 'review', 'signature', 'table'])

/** Cell types that never hold a typed value. */
const NOT_A_CELL_VALUE = new Set(['calculated', 'description'])

export interface LabTarget {
  /** A field id, or a table cell key `${rowId}__${colId}`. */
  id: string
  /** "TNC Pre", or "CD3 % / Pre-Freeze" for a cell. */
  label: string
  /** The table field a cell is in; null otherwise. */
  tableFieldId: string | null
  testCode: string | null
  /** Must hold a value before the results can be applied. */
  mustFill: boolean
  /**
   * The input the Apply Test Results dialog offers when this value is missing,
   * so it can be entered right there. Null for a type that only the form itself
   * can take (a dropdown, a checkbox): that one is entered on the form.
   */
  inputType: LabInputType | null
}

export type LabInputType = 'number' | 'text' | 'date' | 'datetime-local'

/** The dialog's input for a field or cell type, as the renderer draws it. */
function inputTypeOf(type: string): LabInputType | null {
  if (type === 'number') return 'number'
  if (type === 'text' || type === 'textarea') return 'text'
  if (type === 'date') return 'date'
  if (type === 'datetime') return 'datetime-local'
  return null
}

type Candidate = Omit<LabTarget, 'testCode' | 'mustFill'> & { required: boolean }

function blank(v: unknown): boolean {
  return v == null || (typeof v === 'string' && v.trim() === '')
}

/** Every stage some value on this form is set to, in the order they appear. */
export function stagesOnForm(def: FormDefinition): string[] {
  const seen: string[] = []
  for (const t of candidates(def)) {
    const stage = lisStageOfTarget(def, t.id)
    if (stage && !seen.includes(stage)) seen.push(stage)
  }
  return seen
}

/**
 * The values of one stage. `stage` null is for a form with no stages, where it
 * means every value connected to a test code. A value must be filled when its
 * field is required or it is connected to a test code (a table cell has no
 * "required" of its own: the test code is what makes it a laboratory result).
 */
export function labTargets(def: FormDefinition, stage: string | null): LabTarget[] {
  return candidates(def).flatMap(t => {
    const testCode = lisTestCodeOfTarget(def, t.id)
    const inScope =
      stage === null ? testCode !== null : lisStageOfTarget(def, t.id) === stage
    if (!inScope) return []
    const { required, ...rest } = t
    return [{ ...rest, testCode, mustFill: required || testCode !== null }]
  })
}

/** The values of a stage that must be filled and are not. */
export function missingLabValues(
  def: FormDefinition,
  stage: string | null,
  values: Record<string, unknown>,
): LabTarget[] {
  return labTargets(def, stage).filter(t => t.mustFill && blank(values[t.id]))
}

type Receivable = Pick<CreatedProduct, 'id' | 'supersededById' | 'volumeMl' | 'parentProductId'>

/**
 * Products that can receive results: any of the procedure's products, aliquots
 * included, except a re-coded one (its successor receives them) and one used
 * up by a split (0 mL; its aliquots receive them).
 */
export function receivingProducts<T extends Receivable>(products: readonly T[]): T[] {
  return products.filter(p => !isSuperseded(p) && !isUsedUp(p, products))
}

/**
 * The product the dialog starts on: the only one there is, else the form's own
 * single working product when it can receive results. Otherwise the user picks.
 */
export function initialReceivingProduct<T extends Pick<CreatedProduct, 'id'>>(
  receivers: readonly T[],
  workingIds: readonly string[],
): T | null {
  if (receivers.length === 1) return receivers[0]
  if (workingIds.length !== 1) return null
  return receivers.find(p => p.id === workingIds[0]) ?? null
}

function candidates(def: FormDefinition): Candidate[] {
  const out: Candidate[] = []
  for (const f of allFields(def)) {
    const type = f.fieldType
    if (type === 'table') {
      const grid = asTableConfig(f.tableConfig)
      if (grid) out.push(...cellsOf(grid, f.id))
      continue
    }
    if (NOT_A_VALUE.has(type) || isConstantField(type) || valueIsAttachment(type)) continue
    out.push({
      id: f.id,
      label: f.label || f.id,
      tableFieldId: null,
      required: f.required === true,
      inputType: inputTypeOf(type),
    })
  }
  const grid = asTableConfig((def as { tableConfig?: unknown }).tableConfig)
  if (grid) out.push(...cellsOf(grid, null))
  return out
}

function cellsOf(config: TableConfig, tableFieldId: string | null): Candidate[] {
  const out: Candidate[] = []
  for (const row of config.rows ?? []) {
    for (const col of config.columns) {
      const resolved = resolveCell(config, row.id, col)
      if (resolved.disabled || NOT_A_CELL_VALUE.has(resolved.type)) continue
      out.push({
        id: cellKey(row.id, col.id),
        label: `${row.label || row.id} / ${col.label || col.id}`,
        tableFieldId,
        required: false,
        inputType: inputTypeOf(resolved.type),
      })
    }
  }
  return out
}
